<script setup lang="ts">
import { Plus } from 'lucide-vue-next'
import DateFilters from '../DateFilters.vue'
import MarketingFlowTriggerTypeBaseWrap from './BaseWrap.vue'

import type { Audience } from '@/types/types'
import type { Store } from '@/views/contests/Components/types/global'
import type { TriggerSettings } from '../../types/marketingFlow'

const { t } = useI18n()

defineProps<{
  audiences: Audience[]
  stores: Store[]
}>()
const triggerSettings = defineModel<TriggerSettings>('triggerSettings')

const { getTriggerStepErrors } = useMarketingFlow()

const emit = defineEmits<{
  (e: 'onChanges', event): void
}>()

const showSelectCheckinCount = ref(triggerSettings.value.checkinCount !== null)
const options = computed(() => {
  const base = {
    1: 'first',
    2: 'second',
    3: 'third',
    4: 'fourth',
    5: 'fifth',
    custom: 'custom'
  }

  return Object.entries(base).map(([key, value]) => ({
    label: t(`pages.marketingFlow.marketingFlowEditPage.trigger.visitOrOrderCount.${value}`),
    value: key
  }))
})
const handleAddVisitOrOrder = () => {
  showSelectCheckinCount.value = true
  triggerSettings.value.checkinCount = '1'
}

const deleteCheckinCount = () => {
  triggerSettings.value.checkinCount = null
  triggerSettings.value.customCheckinCount = null
  showSelectCheckinCount.value = false
}

const checkinCountChange = (value: string) => {
  emit('onChanges', { isCritical: true, field: 'checkinCount', value: value })

  if (triggerSettings.value.customCheckinCount && value !== 'custom') {
    triggerSettings.value.customCheckinCount = null
  }
}

const triggerStepErrors = computed(() => {
  return getTriggerStepErrors(triggerSettings.value)
})

const customCheckinCountError = computed(() => {
  const customCheckinCountErrors = triggerStepErrors.value.find((error) => error.field === 'customCheckinCount')

  return customCheckinCountErrors && triggerSettings.value.customCheckinCount !== null
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${customCheckinCountErrors?.i18nKey}`)]
    : null
})
</script>

<template>
  <div>
    <MarketingFlowTriggerTypeBaseWrap
      :deleteable="triggerSettings.checkinCount !== null"
      icon="IconoirUserBag"
      :title="t('pages.marketingFlow.marketingFlowEditPage.trigger.checkinCountLabel')"
      @delete="deleteCheckinCount"
    >
      <Button v-if="!showSelectCheckinCount" variant="outline" class="w-full py-3" @click="handleAddVisitOrOrder">
        <Plus />
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.addVisitOrOrder') }}
      </Button>
      <div v-else>
        <div class="text-sm mb-1.5 text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.visitOrOrderCount.label') }}
        </div>
        <Select v-model="triggerSettings.checkinCount" @update:model-value="checkinCountChange">
          <SelectTrigger class="h-10 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in options" :key="option.value" :value="option.value">
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
        <div v-if="triggerSettings.checkinCount === 'custom'" class="mt-1.5">
          <AppFormInput
            id="checkinCount"
            v-model="triggerSettings.customCheckinCount"
            type="number"
            :min="1"
            class="mt-3"
            :errors="customCheckinCountError"
            error-classes="text-xs"
            @update:model-value="$emit('onChanges', { isCritical: true, field: 'customCheckinCount', value: $event })"
          >
            <template #icon-right>
              <span class="whitespace-nowrap text-sm text-muted-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.checkinCount') }}
              </span>
            </template>
          </AppFormInput>
        </div>
      </div>
    </MarketingFlowTriggerTypeBaseWrap>

    <div class="mb-0 flex w-full flex-row pr-5">
      <DateFilters
        v-model="triggerSettings"
        :audiences="audiences"
        :stores="stores"
        @on-changes="emit('onChanges', $event)"
      />
    </div>
  </div>
</template>
